import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import fields from "./json/PurchaseReturnFeilds.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import { validateForm } from "utils/validation";
import { PurchaseReturnRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import PurchaseReturnListForm from "./Components/PurchaseReturnListForm";
import {
  Search,
  Create,
  getInvoice,
  getSuppliers
} from "adminApi/PurchaseReturnApi/PurchaseReturn";

const PurchaseReturnList = () => {
  const navigate = useNavigate();
  const { formdata } = fields;
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [searchData, setSearchData] = useState(null);

  const initialValues = {...formdata.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {}),
    items: [],
    return_date: "",
    reason: "",
    total_amount: 0,
    stock:0,
  };

  const {
    values,
    errors,
    handleChange,
    setValues,
    setErrors
  } = useFormChange(initialValues, PurchaseReturnRules);

  // Load supplier dropdown
  const {
    data: supplierOptions,
    loading: loadingSupplier
  } = useFetchAllData(getSuppliers);

  // Load invoices when supplier changes
  useEffect(() => {
    if (!values.supplier_id) return setInvoiceOptions([]);

    let cancel = false;

    (async () => {
      setLoadingInvoice(true);
      try {
        const res = await getInvoice(values.supplier_id);
        if (!cancel && res?.data?.success) {
          setInvoiceOptions(res.data.data || []);
        }
      } finally {
        if (!cancel) setLoadingInvoice(false);
      }
    })();

    return () => (cancel = true);
  }, [values.supplier_id]);

  // Search Purchase Invoice items
  const handleSearch = async () => {
    const validationErrors = validateForm(values, PurchaseReturnRules);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      const res = await Search({ data: values });
      const payload = res?.data?.data?.data ?? res?.data?.data ?? {};
      setSearchData(payload);
    } catch (e) {
      console.error("Search Error", e);
    }
  };

  // Merge items correctly (fix quantity → purchase_quantity)
  useEffect(() => {
    if (!searchData?.items?.length) return;

    const merged = searchData.items.reduce((acc, it) => {
      const key = `${it.product_id}-${String(it.product_rate)}`;
      const purchaseQty = Number(it.purchase_quantity || it.quantity || 0);

      if (!acc[key]) {
        acc[key] = {
          product_id: it.product_id,
          stock: it.stock,
          product_name: it.product_name,
          purchase_quantity: purchaseQty,
          return_quantity: 0,
          product_rate: Number(it.product_rate || 0),
          discount: Number(it.discount || 0),
          total: Number(it.total || 0),
        };
      } else {
        acc[key].purchase_quantity += purchaseQty;
        acc[key].total += Number(it.total || 0);
      }
      return acc;
    }, {});

    const itemsArray = Object.values(merged);

    setValues((prev) => ({
      ...prev,
      items: itemsArray,
      return_date: searchData.date || prev.return_date || "",
      reason: searchData.reason || "",
      total_amount: itemsArray.reduce((s, x) => s + Number(x.total || 0), 0),
    }));
  }, [searchData, setValues]);

  // SUBMIT
  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      const payload = {
        supplier_id: formValues.supplier_id,
        purchase_invoice_id: formValues.purchase_invoice_id,
        return_date: formValues.return_date,
        return_reason: formValues.reason,
        total_amount: Number(formValues.total_amount || 0),

        items: formValues.items
          .filter((it) => Number(it.return_quantity) > 0) // only return qty > 0
          .map((it) => ({
            product_id: it.product_id,
            stock: it.stock,
            quantity: Number(it.return_quantity || 0),
            product_rate: Number(it.product_rate || 0),
            discount: Number(it.discount || 0),
            total: Number(it.total || 0),
          })),
      };

      return await Create({ data: payload });
    },

    (vals) => validateForm(vals, PurchaseReturnRules),
    () => navigate(-1),
    "Purchase Return Created Successfully"
  );


  return (
    <>
      {(loadingSubmit || loadingSupplier || loadingInvoice) && <Spinner isSpinner />}

      <PurchaseReturnListForm
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        handleSearch={handleSearch}
        loading={loadingSubmit}
        supplierOptions={supplierOptions || []}
        invoiceOptions={invoiceOptions || []}
        setValues={setValues}
      />
    </>
  );
};

export default PurchaseReturnList;
